import React from 'react';
import Modal from './Modal';
import { ModalProps, ModalSize } from './Modal.types';
import { Button, ButtonType, ButtonSize } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';

export interface ConfirmationModalProps extends Omit<ModalProps, 'children'> {
  /**
   * The message to display in the confirmation modal
   */
  message: string;
  /**
   * The label for the confirm button
   */
  confirmLabel?: string;
  /**
   * The label for the cancel button
   */
  cancelLabel?: string;
  /**
   * Function to call when the user confirms the action
   */
  onConfirm: () => void;
  /**
   * Variant to determine button colors/styles (e.g., "danger" for delete actions)
   */
  variant?: 'default' | 'danger';
  /**
   * Whether the confirm action is loading
   */
  isLoading?: boolean;
  /**
   * Whether to show the confirm button (default: true)
   */
  showConfirmButton?: boolean;
  /**
   * Whether the action is destructive (uses destructive button style)
   */
  isDestructive?: boolean;
}

/**
 * A generic confirmation modal component for confirming user actions
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  isOpen,
  onClose,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  variant = 'default',
  size = ModalSize.Small,
  showCloseButton = true,
  className = '',
  isLoading = false,
  showConfirmButton = true,
  isDestructive = false,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={title ?? t(TranslationKeyEnum.ConfirmAction)}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      showCloseButton={showCloseButton}
      className={className}
    >
      <div className="flex flex-col gap-200">
        <p className="text-body-100 text-gray-800">{message}</p>

        <div className="flex justify-end gap-150 mt-200">
          <Button
            label={cancelLabel ?? t(TranslationKeyEnum.Cancel)}
            type={ButtonType.Secondary}
            size={ButtonSize.Medium}
            onClick={onClose}
            disabled={isLoading}
          />
          {showConfirmButton && (
            <Button
              label={confirmLabel ?? t(TranslationKeyEnum.ConfirmAction)}
              type={ButtonType.Primary}
              destructive={isDestructive ?? variant === 'danger'}
              size={ButtonSize.Medium}
              onClick={onConfirm}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
